import { devtools, persist } from 'zustand/middleware';
import { create, StateCreator } from 'zustand';
import { deviceType } from 'react-device-detect';
import { setSession } from '@/utils';
import { login } from '@/services';
import { AuthStateType, FirebaseContextType, UserCustom } from '@/context/auth/types';
import { getPermissions } from '@/services/subscriptions';

const initialState: AuthStateType = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
  subscriptions: {
    loading: false,
    data: null,
  },
};
const createUserStore: StateCreator<FirebaseContextType> = (set, get) => ({
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  subscriptions: {
    loading: false,
    data: null,
  },
  login: async (email: string, password: string) => {
    try {
      const result = await login({ email, password, device: deviceType });
      await setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
    } catch (e) {
      await setSession({});
      console.info(e);
      return e;
    }
  },
  register: async (email: string, password: string, firstName: string, lastName: string) => {},
  logout: async () => {
    setSession({});
    localStorage.clear();
    set(() => ({
      ...(initialState as any),
      isAuthenticated: false,
      isInitialized: true,
      subscriptions: {
        loading: false,
        data: null,
      },
    }));
  },
  setInitialized: (value: boolean) => {
    set(() => ({ isInitialized: value }));
  },
  init: async (user: any) => {
    get().setInitialized(true);
    try {
      const branches = user.branches.filter(
        (branch: any) => branch?.companyId === user.companySelected?.id
      );
      const userData: UserCustom = {
        ...user,
        displayName: user.name ?? user.email ?? '',
        id: user.id,
        companySelected: user?.companySelected ?? undefined,
        branchSelected: user?.branchSelected ?? undefined,
        companies: user.companies,
        branches,
        role: user.rol,
      };
      set(() => ({
        isAuthenticated: true,
        isInitialized: true,
        user: userData,
      }));
    } catch (e) {
      /* empty */
    }
  },
  updateUserState: (values: any) => {
    const currentValues = get().user;
    const mapper: UserCustom = {
      ...currentValues,
      id: values.id,
      companySelected: values?.companySelected,
      branchSelected: values?.branchSelected,
      companies: values.companies,
      branches: values.branches,
      role: values.rol ?? values.role,
    } as UserCustom;
    set(() => ({
      user: mapper,
    }));
  },
  updateBranches: (branches: any) => {
    const currentValues = get().user;
    const mapper: UserCustom = {
      ...currentValues,
      branches,
    } as UserCustom;
    set(() => ({
      user: mapper,
    }));
  },
  getPermissions: async ({ queryClient }) => {
    try {
      set(() => ({ subscriptions: { loading: true, data: null } }));
      if (!get().user?.companySelected?.id) {
        set(() => ({ subscriptions: { loading: false, data: null } }));
      } else {
        const permissions = await getPermissions(get().user?.companySelected?.id ?? '');
        set(() => ({ subscriptions: { loading: false, data: permissions } }));
      }
    } catch {
      set(() => ({ subscriptions: { loading: false, data: null } }));
    } finally {
      await queryClient.invalidateQueries();
    }
  },
});
export const UserStore = create<FirebaseContextType>()(
  devtools(
    persist(createUserStore, {
      name: 'user-storage',
    })
  )
);
